import { useNavigate } from "@tanstack/react-router";
import { MessageCircle, User, UserX, X } from "lucide-react";
import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useBlockedUsersList } from "@/hooks/useBlockedUsersList";
import { useUserBlockActions } from "@/hooks/useUserBlockActions";
import { buildUserProfileFromPath } from "@/lib/navigation/userProfileNavigation";
import { isSameUserId } from "@/lib/utils/userId";
import { useGlobalUserInfo } from "@/providers/AuthProvider";

interface UserInfo {
  id: number;
  name: string;
  avatarUrl: string | null;
}

interface UserProfilePopupProps {
  user: UserInfo;
  isVisible: boolean;
  onClose: () => void;
  onSendMessage?: (userId: number) => void;
  isCurrentUser?: boolean;
  isLoading?: boolean;
  position?: "left" | "right";
}

export const UserProfilePopup: React.FC<UserProfilePopupProps> = ({
  user,
  isVisible,
  onClose,
  onSendMessage,
  isCurrentUser = false,
  isLoading = false,
  position = "left",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUserInfo = useGlobalUserInfo();
  const isHandlingActionRef = useRef(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const { data: blockedUsers } = useBlockedUsersList();
  const {
    blockUserWithFeedback,
    unblockUserWithFeedback,
    isBlocking,
    isUnblocking,
  } = useUserBlockActions();

  // Fallback check to ensure we correctly identify current user
  // This handles cases where isCurrentUser prop might not be passed correctly
  const isActuallyCurrentUser =
    isCurrentUser || isSameUserId(currentUserInfo?.id, user.id);

  const isBlocked =
    Array.isArray(blockedUsers) && blockedUsers.includes(String(user.id));
  const dialogTitle = isBlocked
    ? t("common.confirmUnblockTitle")
    : t("common.confirmBlockTitle");
  const dialogDescription = isBlocked
    ? t("common.confirmUnblockDescription", { userName: user.name })
    : t("common.confirmBlockDescription", { userName: user.name });

  if (!isVisible && !isConfirmDialogOpen) return null;

  const handleViewProfile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isHandlingActionRef.current) return;
    isHandlingActionRef.current = true;

    const from = buildUserProfileFromPath(
      window.location.pathname,
      window.location.search,
      window.location.hash,
    );

    // 先にポップアップを閉じる
    onClose();

    // ナビゲーション処理を少し遅延させる
    setTimeout(() => {
      navigate({
        to: "/user-profile/$user-id",
        params: {
          "user-id": String(user.id),
        },
        search: from ? { from } : {},
      });
      isHandlingActionRef.current = false;
    }, 100);
  };

  const handleSendMessage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isHandlingActionRef.current) return;
    isHandlingActionRef.current = true;

    if (onSendMessage) {
      // 先にポップアップを閉じる
      onClose();

      // メッセージ送信処理を少し遅延させる
      setTimeout(() => {
        onSendMessage(user.id);
        isHandlingActionRef.current = false;
      }, 100);
    } else {
      console.warn("onSendMessage is not defined");
      isHandlingActionRef.current = false;
    }
  };

  const handleUserBlock = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isHandlingActionRef.current) return;

    // 確認ダイアログを開く（ポップアップは開いたまま）
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmBlock = async () => {
    if (isHandlingActionRef.current) return;
    isHandlingActionRef.current = true;

    try {
      if (isBlocked) {
        await unblockUserWithFeedback(user.id);
      } else {
        await blockUserWithFeedback(user.id);
      }
      setIsConfirmDialogOpen(false);
      onClose();
    } catch (error) {
      console.error("Error blocking/unblocking user:", error);
    } finally {
      isHandlingActionRef.current = false;
    }
  };

  const positionClasses = position === "right" ? "right-2" : "left-2";
  const arrowPositionClasses =
    position === "right" ? "top-4 -left-1" : "top-4 -left-2";

  return (
    <>
      {isVisible && (
        <div
          className={`absolute ${positionClasses} top-full z-[var(--z-profile-popup)] mt-2 w-80 rounded-lg border border-gray-200 bg-white p-4 shadow-sm`}
          style={{ pointerEvents: "auto" }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          {/* 吹き出しの矢印 - points toward the avatar based on popup position */}
          <div
            className={`absolute ${arrowPositionClasses} size-4 -rotate-45 border-l border-t border-gray-200 bg-white`}
          />

          {/* プロファイル情報 */}
          <div className="mb-3 flex items-start gap-3">
            <div className="size-16 shrink-0 overflow-hidden rounded-tr-lg bg-gray-200">
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.name}
                  className="size-full rounded-tr-lg object-cover"
                />
              ) : (
                <div className="flex size-full items-center justify-center rounded-tr-lg text-xl font-bold">
                  {user.name[0]}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-lg font-semibold text-gray-900">
                {user.name}
              </h3>
            </div>
            <Button
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
            >
              <X size={16} className="text-gray-400" />
            </Button>
          </div>

          {/* アクションボタン */}
          <div className="flex gap-2">
            {/* プロフィール表示ボタン - userIdがあれば常に表示 */}
            <Button
              variant="teal"
              onClick={handleViewProfile}
              disabled={isLoading || isHandlingActionRef.current}
            >
              <User size={16} />
              <span className="text-sm font-medium">
                {isActuallyCurrentUser
                  ? t("common.myPage")
                  : t("common.profile")}
              </span>
            </Button>

            {/* メッセージボタン - 自分でない場合かつ送信処理がある場合に表示 */}
            {!isActuallyCurrentUser && !isBlocked && onSendMessage && (
              <Button
                variant="teal"
                onClick={handleSendMessage}
                disabled={isLoading || isHandlingActionRef.current}
              >
                <MessageCircle size={16} />
              </Button>
            )}
            {!isActuallyCurrentUser && (
              <Button
                variant={isBlocked ? "secondary" : "destructive"}
                onClick={handleUserBlock}
                disabled={
                  isLoading ||
                  isHandlingActionRef.current ||
                  isBlocking ||
                  isUnblocking
                }
              >
                <UserX size={16} />
              </Button>
            )}
          </div>
        </div>
      )}

      <ConfirmationDialog
        open={isConfirmDialogOpen}
        onOpenChange={setIsConfirmDialogOpen}
        title={dialogTitle}
        description={dialogDescription}
        cancelText={t("common.cancel")}
        confirmText={isBlocked ? t("common.unblock") : t("common.block")}
        isLoading={isBlocking || isUnblocking}
        onConfirm={handleConfirmBlock}
        variant={isBlocked ? "secondary" : "destructive"}
        contentProps={{ "data-user-popup-block-dialog": "true" }}
      />
    </>
  );
};
